import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  GraphQLAPI,
  GraphQLResult,
  queries,
  GraphQLQuery,
} from "@examspark/api";
import { collectAllPages } from "@examspark/common";
import {
  IDSK,
  QuerySearchArgs,
  Resource,
  SearchableResourceConnectionNew,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";

export const useQueryIsUserFavorite = (item?: IDSK, userId?: string) => {
  const favoritesQuery = useQuerySearchUserFavorites(userId);
  return useQuery({
    enabled: item != null && userId != null && favoritesQuery.data != null,
    queryKey: ["is-user-favorite", userId, item?.id],
    queryFn: async () =>
      !!favoritesQuery.data?.find(
        (r) => r.id === item?.id && r.sk === item?.sk,
      ),
  });
};

export const useQuerySearchUserFavorites = (userId?: string) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    enabled: !!userId,
    queryKey: ["user-favorites", userId],
    queryFn: async () => {
      const pages = await collectAllPages(async (nextToken) => {
        const result = (await GraphQLAPI.query({
          query: queries.search.search({
            label: "SearchUserFavorites",
            fields: `
              id
              sk
              name
              type
              workspaceId
              bankId
              idInc
              createdAt
              updatedAt
              subject

              createdAt
              updatedAt
            `,
          }),
          variables: {
            authorizationContext,
            sort: [
              {
                field: "updatedAt",
                direction: "desc",
              },
            ],

            filter: {
              favoriteUserIds: {
                eq: userId,
              },
            },

            nextToken,
          } as QuerySearchArgs,
        })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

        return result.data?.search;
      });

      return pages.flatMap((p) => p.items ?? []);
    },
  });
};

const useUpdateQueryDataIsUserFavorite = () => {
  const client = useQueryClient();

  return useCallback(
    async (favorite: IDSK, userId: string, isUserFavorite: boolean) => {
      if (!isUserFavorite) {
        client.setQueryData(
          ["user-favorites", userId],
          (data: IDSK[] | undefined) =>
            data?.filter(
              (f) => !(f.id === favorite.id && f.sk === favorite.sk),
            ),
        );
      } else {
        const favoriteData = await GraphQLAPI.query<
          GraphQLQuery<{ getResource: Resource }>
        >({
          query: /* GraphQL */ `
            query GetFavoriteData($id: ID!, $sk: String!) {
              getResource(id: $id, sk: $sk) {
                id
                sk
                name
                type
                createdAt
                updatedAt
              }
            }
          `,
          variables: {
            id: favorite.id,
            sk: favorite.sk,
          },
        });

        client.setQueryData(
          ["user-favorites", userId],
          (data: Resource[] | undefined) => {
            if (favoriteData.data?.getResource == null) return;
            if (data == null) return [favoriteData.data.getResource];
            if (
              data?.some((r) => r.id === favorite.id && r.sk === favorite.sk)
            ) {
              return data;
            }
            return [...data, favoriteData.data.getResource];
          },
        );
      }

      client.setQueryData(
        ["is-user-favorite", userId, favorite.id],
        () => isUserFavorite,
      );
    },
    [client],
  );
};

export const useMutationAddFavorite = () => {
  const setIsUserFavorite = useUpdateQueryDataIsUserFavorite();

  return useMutation({
    mutationFn: async (input: {
      id: string;
      sk: string;
      workspaceId: string;
      userId: string;
    }) => {
      const result = (await GraphQLAPI.query({
        query: queries.favorites.addFavorite,
        variables: {
          input,
        },
      })) as GraphQLResult<{ createResourceFavorite: Resource }>;

      const favorite = result.data?.createResourceFavorite;

      if (favorite == null) return false;

      setIsUserFavorite(input, input.userId, true);
      return true;
    },
  });
};

export const useMutationRemoveFavorite = () => {
  const setIsUserFavorite = useUpdateQueryDataIsUserFavorite();

  return useMutation({
    mutationFn: async (input: { id: string; sk: string; userId: string }) => {
      const result = (await GraphQLAPI.query({
        query: queries.favorites.removeFavorite,
        variables: {
          input,
        },
      })) as GraphQLResult<{ deleteResourceFavorite: Resource }>;

      const favorite = result.data?.deleteResourceFavorite;
      if (favorite == null) return false;

      setIsUserFavorite(input, input.userId, false);
      return true;
    },
  });
};
